/**
 * Subscription context + feature-gate utilities.
 *
 * Loads `/subscription/me` once per session and exposes the current tier,
 * feature flags, and usage. Other components can do:
 *   const { hasFeature, tier, usage, limits } = useSubscription();
 *   if (!hasFeature("whatsapp_share")) return <Upgrade requires="premium" />;
 *
 * The context only renders its children after the first load so feature
 * checks don't briefly return `false` on mount.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/App";

const SubscriptionContext = createContext(null);

const FEATURE_LABELS = {
  core_crud: "CRUD básico",
  catalog_export: "Catálogo PDF / CSV",
  whatsapp_chat: "Chat WhatsApp directo",
  inventory: "Inventario + Kardex",
  invoicing: "Facturación",
  receivables: "Cuentas por cobrar",
  costing: "Costeo",
  purchase_orders: "Órdenes de compra",
  backup: "Respaldo / Restauración",
  whatsapp_share: "Compartir facturas/pedidos por WhatsApp",
  ai_order_image: "Pedido desde foto (IA)",
  insights: "Insights + Pronóstico",
  route_planning: "Planificación de rutas",
  route_optimization: "Optimización automática de rutas",
  hr_module: "Recursos Humanos (Vacaciones, Ausencias, Nómina)",
  public_links: "Enlaces públicos para clientes",
  whatsapp_advanced: "WhatsApp avanzado (estado de cuenta, recordatorios)",
};

const TIER_LABEL = { basic: "Básico", premium: "Premium", pro: "Pro" };
const TIER_COLOR = {
  basic: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-300" },
  premium: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-300" },
  pro: { bg: "bg-amber-100", text: "text-amber-900", border: "border-amber-400" },
};

export function SubscriptionProvider({ children }) {
  const [state, setState] = useState({ loading: true, sub: null, features: [], limits: {}, usage: {}, isAdmin: false });

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/subscription/me");
      setState({
        loading: false,
        sub: data.subscription,
        features: data.features || [],
        limits: data.limits || {},
        usage: data.usage || {},
        isAdmin: !!data.is_admin,
      });
    } catch {
      // not logged in / network down — fall back to "basic" so UI works
      setState({ loading: false, sub: { tier: "basic", status: "active" }, features: ["core_crud", "catalog_export", "whatsapp_chat"], limits: {}, usage: {}, isAdmin: false });
    }
  }, []);

  useEffect(() => {
    const tok = localStorage.getItem("token");
    if (tok) reload();
    else setState(s => ({ ...s, loading: false }));

    const handler = () => reload();
    window.addEventListener("subscription-updated", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("subscription-updated", handler);
      window.removeEventListener("storage", handler);
    };
  }, [reload]);

  const value = useMemo(() => ({
    ...state,
    tier: state.sub?.tier || "basic",
    hasFeature: (k) => state.features.includes(k),
    reload,
  }), [state, reload]);

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    return {
      loading: false,
      tier: "basic",
      features: [],
      limits: {},
      usage: {},
      sub: null,
      hasFeature: () => false,
      reload: () => Promise.resolve(),
      isAdmin: false,
    };
  }
  return ctx;
}

export function tierLabel(t) { return TIER_LABEL[t] || t; }
export function tierBadgeClass(t) {
  const c = TIER_COLOR[t] || TIER_COLOR.basic;
  return `${c.bg} ${c.text} ${c.border}`;
}
export function featureLabel(k) { return FEATURE_LABELS[k] || k; }
